/**
 * Web Vitals Tracking
 *
 * Measures Core Web Vitals (LCP, FID, CLS, INP, TTFB) and reports them
 * to Firebase Analytics for SEO monitoring and performance optimization.
 *
 * These metrics directly impact Google search rankings.
 */

import { onCLS, onFID, onLCP, onINP, onTTFB, type Metric } from 'web-vitals';
import { logEvent, type Analytics } from 'firebase/analytics';
import { Logger } from './Logger';

const logger = Logger.getLogger('WebVitals');

interface VitalsReport {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
}

// Store analytics instance for use in the callback
let analyticsInstance: Analytics | null = null;

/**
 * Send vitals to Firebase Analytics
 */
function sendToAnalytics(metric: Metric): void {
  const report: VitalsReport = {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType || 'unknown',
  };

  // Log in development
  if (import.meta.env.DEV) {
    logger.debug(`${report.name}: ${report.value.toFixed(2)} (${report.rating})`);
  }

  // Send to Firebase Analytics
  if (analyticsInstance) {
    logEvent(analyticsInstance, 'web_vitals', {
      metric_name: metric.name,
      metric_value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_rating: metric.rating,
      metric_delta: Math.round(metric.delta),
      metric_id: metric.id,
      navigation_type: metric.navigationType || 'unknown',
    });
  }
}

/**
 * Initialize Web Vitals tracking
 * Pass the Firebase Analytics instance from your config
 *
 * @param analytics - Firebase Analytics instance from src/firebase/config.ts
 *
 * @example
 * import { analytics } from './firebase/config';
 * import { initWebVitals } from './utils/webVitals';
 * initWebVitals(analytics);
 */
export function initWebVitals(analytics: Analytics | null): void {
  analyticsInstance = analytics;

  if (!analytics) {
    logger.warn('Web Vitals initialized without analytics - metrics will only log to console');
  } else {
    logger.info('Web Vitals tracking initialized with Firebase Analytics');
  }

  // Largest Contentful Paint - measures loading performance
  // Good: < 2.5s, Needs Improvement: 2.5-4s, Poor: > 4s
  onLCP(sendToAnalytics);

  // First Input Delay - measures interactivity (legacy, being replaced by INP)
  // Good: < 100ms, Needs Improvement: 100-300ms, Poor: > 300ms
  onFID(sendToAnalytics);

  // Cumulative Layout Shift - measures visual stability
  // Good: < 0.1, Needs Improvement: 0.1-0.25, Poor: > 0.25
  onCLS(sendToAnalytics);

  // Interaction to Next Paint - measures responsiveness (replacing FID)
  // Good: < 200ms, Needs Improvement: 200-500ms, Poor: > 500ms
  onINP(sendToAnalytics);

  // Time to First Byte - measures server responsiveness
  // Good: < 800ms, Needs Improvement: 800-1800ms, Poor: > 1800ms
  onTTFB(sendToAnalytics);
}

/**
 * Get performance summary for debugging
 */
export function getPerformanceSummary(): Record<string, number> {
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

  return {
    dnsLookup: navigation?.domainLookupEnd - navigation?.domainLookupStart,
    tcpConnection: navigation?.connectEnd - navigation?.connectStart,
    tlsNegotiation: navigation?.requestStart - navigation?.secureConnectionStart,
    serverResponse: navigation?.responseStart - navigation?.requestStart,
    domParsing: navigation?.domInteractive - navigation?.responseEnd,
    resourceLoading: navigation?.loadEventStart - navigation?.domContentLoadedEventEnd,
    totalLoadTime: navigation?.loadEventEnd - navigation?.startTime,
  };
}
